// Мелкие общие части экранов журнала здоровья.

import { useState, type ReactNode } from 'react'

import { Button, ErrorText, Modal } from '@/components/ui'
import { cn } from '@/lib/cn'

/** Строка над содержимым вкладки: пояснение слева, главная кнопка справа. */
export function TabToolbar({ description, action }: { description?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <p className="min-w-0 flex-1 text-sm text-muted">{description}</p>
      {action}
    </div>
  )
}

/** Кнопка «Удалить» с подтверждением: удаление можно откатить через журнал изменений. */
export function DeleteButton({
  what,
  onConfirm,
  pending,
  error,
}: {
  what: string
  onConfirm: () => Promise<unknown>
  pending?: boolean
  error?: unknown
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)} aria-label={`Удалить: ${what}`}>
        Удалить
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Удалить запись?"
        description={`${what}. Удаление можно отменить в журнале изменений.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Оставить
            </Button>
            <Button
              variant="danger"
              loading={pending}
              onClick={() => onConfirm().then(() => setOpen(false), () => undefined)}
            >
              Удалить
            </Button>
          </>
        }
      >
        <ErrorText error={error} />
      </Modal>
    </>
  )
}

/** Оценка 1–5 одним тапом. */
export function Rating({
  label,
  value,
  onChange,
  captions,
  allowEmpty,
  error,
}: {
  label: string
  value: number | null
  onChange: (value: number | null) => void
  captions?: readonly string[]
  allowEmpty?: boolean
  error?: ReactNode
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-fg">{label}</span>
      <div className="grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((n) => {
          const active = value === n
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(active && allowEmpty ? null : n)}
              className={cn(
                'flex min-h-14 flex-col items-center justify-center rounded-lg border text-lg font-semibold tabular-nums',
                active ? 'border-accent bg-accent-soft text-accent' : 'border-border bg-surface text-fg hover:bg-surface-2',
              )}
            >
              {n}
              {captions?.[n - 1] ? <span className="text-[11px] font-normal text-muted">{captions[n - 1]}</span> : null}
            </button>
          )
        })}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
}
